import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Shuffle, Wallet, X, Trash2, Info, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Alert, AlertDescription, AlertTitle } from "../ui/alert";
import { api, errorMessage } from "../../lib/api";
import { nok } from "../../lib/basar";
import { useLang } from "../../context/LanguageContext";
import { BuySteps } from "./BuySteps";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export function normalizePhone(raw) {
  let digits = String(raw || "").replace(/[\s.\-()]/g, "");
  if (digits.startsWith("+47")) digits = digits.slice(3);
  else if (digits.startsWith("0047")) digits = digits.slice(4);
  return /^\d{8}$/.test(digits) ? digits : "";
}

/**
 * Kjøpspanel for ein basar: valde nummer, tilfeldig forslag, kjøparskjema (namn + mobil påkravd, e-post valfri) og Vipps-CTA.
 * Betalinga går direkte til arrangøren si Vipps-salseining; nummera blir haldne i 15 min til betalinga er godkjend.
 * `selected` = Set med nummer. `onRemove(n)`, `onClear()`, `onReplaceSelection(numbers[])`, `onNumbersTaken(numbers[])`.
 */
export const BuyPanel = ({ basar, selected, onRemove, onClear, onReplaceSelection, onNumbersTaken, className = "", id = "basar-buy-panel" }) => {
  const { t } = useLang();
  const min = Math.max(1, Number(basar.min_per_buyer || 1));
  const max = Math.max(min, Number(basar.max_per_order || 100));
  const price = Number(basar.price || 0);
  const count = selected.size;
  const total = count * price;
  const numbers = useMemo(() => [...selected].sort((a, b) => a - b), [selected]);
  const missing = Math.max(0, min - count);
  const remaining = Number(basar.stats?.remaining ?? 0);

  const [quantity, setQuantity] = useState(String(Math.min(min, Math.max(1, remaining || min))));
  const [suggesting, setSuggesting] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "" });
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const suggest = async () => {
    const q = Math.min(max, Math.max(1, parseInt(quantity, 10) || min));
    setSuggesting(true);
    try {
      const { data } = await api.get(`/basar/${basar.id}/suggest`, { params: { quantity: q } });
      onReplaceSelection?.(data.numbers || []);
      toast.success(t("{n} tilfeldige lodd er valgt – du kan bytte dem i rutenettet", { n: (data.numbers || []).length }));
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSuggesting(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting || !basar.can_buy) return;
    if (count < min) {
      toast.error(t("Velg minst {n} lodd", { n: min }));
      return;
    }
    if (count > max) {
      toast.error(t("Maks {n} lodd per kjøp", { n: max }));
      return;
    }
    if (form.name.trim().length < 2) {
      toast.error(t("Fyll inn navnet ditt"));
      return;
    }
    const phone = normalizePhone(form.phone);
    if (!phone) {
      toast.error(t("Fyll inn et gyldig mobilnummer (8 siffer)"));
      return;
    }
    const email = form.email.trim();
    if (email && !EMAIL_RE.test(email)) {
      toast.error(t("E-postadressen ser ikke riktig ut"));
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/basar/${basar.id}/orders`, { numbers, name: form.name.trim(), phone, email });
      if (data.payment_redirect_url) {
        window.location.assign(data.payment_redirect_url);
        return;
      }
      window.location.assign(`/basar/ordre/${data.order.id}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail && typeof detail === "object" && detail.code === "numbers_taken") {
        onNumbersTaken?.(detail.numbers || []);
        toast.error(t("Noen av numrene ble tatt av andre akkurat nå ({numbers}). Velg andre nummer.", { numbers: (detail.numbers || []).join(", ") }));
      } else {
        toast.error(errorMessage(err));
      }
      setSubmitting(false);
    }
  };

  const canPay = basar.can_buy && count >= min && count <= max && !submitting;
  const selectable = basar.status === "open" && basar.in_sale_period && Number(basar.stats?.remaining ?? 0) > 0;
  const noVipps = selectable && !basar.vipps_available;
  // Aktivt kjøpssteg: 1 = vel lodd (under minstekjøp), 2 = opplysningar manglar/ugyldige, 3 = klar til å betale
  const detailsValid = form.name.trim().length >= 2 && !!normalizePhone(form.phone) && (!form.email.trim() || EMAIL_RE.test(form.email.trim()));
  const step = count < min ? 1 : !detailsValid ? 2 : 3;

  return (
    <form id={id} onSubmit={submit} className={`rounded-2xl border border-border bg-card p-5 sm:p-6 space-y-5 scroll-mt-24 ${className}`} data-testid="basar-buy-panel" data-can-buy={basar.can_buy ? "true" : "false"} data-step={noVipps ? undefined : step}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-heading font-semibold text-lg">{noVipps ? t("Velg lodd") : t("Kjøp lodd")}</h2>
          <p className="text-sm text-muted-foreground tabular-nums">{t("{amount} per lodd", { amount: nok(price) })}{min > 1 ? ` · ${t("minst {n}", { n: min })}` : ""}{` · ${t("maks {n}", { n: max })}`}</p>
        </div>
        <Badge variant="outline" className="tabular-nums shrink-0" data-testid="basar-selected-count">{count === 1 ? t("{n} lodd valgt", { n: 1 }) : t("{n} lodd valgt", { n: count })}</Badge>
      </div>

      {/* Steg 1 → 2 → 3 med hjelpetekst: gjer det tydeleg at valet ikkje blir lagra for seg, men blir halde av ved betaling */}
      {!noVipps && <BuySteps current={step} min={min} />}

      {/* Steg 1: valde nummer */}
      <div className="space-y-2" data-testid="basar-selection-section">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {!noVipps && <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-secondary text-[11px] font-semibold tabular-nums text-foreground" aria-hidden>1</span>}
            <Label className="text-sm">{t("Dine nummer")}</Label>
          </div>
          {count > 0 && (
            <button type="button" onClick={onClear} className="text-xs text-muted-foreground inline-flex items-center gap-1 hover:text-foreground transition-colors duration-150" data-testid="basar-clear-selection-button"><Trash2 className="w-3.5 h-3.5" /> {t("Tøm")}</button>
          )}
        </div>
        {count === 0 ? (
          <p className="text-sm text-muted-foreground rounded-lg bg-muted/50 p-3" data-testid="basar-selection-empty">{t("Trykk på ledige nummer i rutenettet, eller la oss velge tilfeldige for deg.")}</p>
        ) : (
          <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto" data-testid="basar-selected-numbers">
            {numbers.map((n) => (
              <button key={n} type="button" onClick={() => onRemove?.(n)} className="inline-flex items-center gap-1 rounded-md border border-[hsl(var(--brand))]/40 bg-[hsl(var(--brand-soft))] px-2 py-1 text-sm font-medium tabular-nums hover:bg-accent transition-colors duration-150" aria-label={t("Fjern lodd {n}", { n })} data-testid={`basar-selected-chip-${n}`}>
                {n} <X className="w-3 h-3 opacity-70" />
              </button>
            ))}
          </div>
        )}
        <div className="flex items-center gap-2">
          <Input value={quantity} onChange={(e) => setQuantity(e.target.value.replace(/\D/g, "").slice(0, 3))} inputMode="numeric" className="h-9 w-20 text-sm tabular-nums" aria-label={t("Antall lodd")} disabled={!selectable} data-testid="basar-suggest-quantity-input" />
          <Button type="button" variant="secondary" size="sm" onClick={suggest} disabled={suggesting || !selectable} data-testid="basar-suggest-button">
            {suggesting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Shuffle className="w-4 h-4 mr-1.5" />} {t("Velg tilfeldige for meg")}
          </Button>
        </div>
        {missing > 0 && count > 0 && <p className="text-xs text-[hsl(var(--warning))]" data-testid="basar-min-missing">{t("Velg {n} til for å nå minstekjøpet", { n: missing })}</p>}
      </div>

      {noVipps ? (
        /* Arrangøren har ikkje Vipps enno: rutenettet kan brukast til å finne ledige nummer, kjøpet skjer hos arrangøren */
        <Alert data-testid="basar-status-no-vipps">
          <Wallet className="h-4 w-4" />
          <AlertTitle>{t("Lodd kjøpes hos arrangøren")}</AlertTitle>
          <AlertDescription className="space-y-1.5">
            <p>{t("Arrangøren har ikke åpnet for Vipps-kjøp på nett ennå. Velg numrene du ønsker og kontakt arrangøren for å kjøpe dem.")}</p>
            {basar.contact_info && <p className="font-medium text-foreground break-words" data-testid="basar-no-vipps-contact">{basar.contact_info}</p>}
            {count > 0 && <p className="tabular-nums" data-testid="basar-no-vipps-selection">{t("Dine nummer: {numbers} ({amount})", { numbers: numbers.join(", "), amount: nok(total) })}</p>}
          </AlertDescription>
        </Alert>
      ) : (
      <>
      {/* Steg 2: kjøparinfo */}
      <div className="space-y-3" data-testid="basar-buyer-details-section">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-secondary text-[11px] font-semibold tabular-nums text-foreground" aria-hidden>2</span>
          <Label className="text-sm">{t("Dine opplysninger")}</Label>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="basar-buyer-name">{t("Navn")}</Label>
          <Input id="basar-buyer-name" value={form.name} onChange={(e) => set("name", e.target.value)} autoComplete="name" required maxLength={120} disabled={!basar.can_buy} data-testid="basar-buyer-name-input" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="basar-buyer-phone">{t("Mobilnummer")}</Label>
          <div className="relative">
            <Smartphone className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input id="basar-buyer-phone" type="tel" inputMode="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} autoComplete="tel" required maxLength={20} placeholder="912 34 567" className="pl-9" disabled={!basar.can_buy} data-testid="basar-buyer-mobile-input" />
          </div>
          <p className="text-xs text-muted-foreground">{t("Brukes til å finne igjen loddene dine («Mine lodd») og hvis du vinner.")}</p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="basar-buyer-email">{t("E-post (valgfritt)")}</Label>
          <Input id="basar-buyer-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} autoComplete="email" maxLength={200} disabled={!basar.can_buy} data-testid="basar-buyer-email-input" />
          <p className="text-xs text-muted-foreground">{t("Oppgir du e-post, får du loddnumrene på e-post og beskjed hvis du vinner.")}</p>
        </div>
      </div>

      {/* Steg 3: sum + CTA */}
      <div className="rounded-xl bg-muted/50 p-4 space-y-3" data-testid="basar-pay-section">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-secondary text-[11px] font-semibold tabular-nums text-foreground" aria-hidden>3</span>
          <Label className="text-sm">{t("Betal")}</Label>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">{count === 1 ? t("{n} lodd × {price}", { n: 1, price: nok(price) }) : t("{n} lodd × {price}", { n: count, price: nok(price) })}</span>
          <span className="font-heading font-semibold text-lg tabular-nums" data-testid="basar-total">{nok(total)}</span>
        </div>
        <Button type="submit" className="w-full h-11 text-base" disabled={!canPay} data-testid="basar-vipps-pay-button">
          {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wallet className="w-4 h-4 mr-2" />}
          {submitting ? t("Sender deg til Vipps …") : count < min ? t("Velg minst {n} lodd", { n: min }) : t("Betal med Vipps – {amount}", { amount: nok(total) })}
        </Button>
        <p className="text-xs text-muted-foreground leading-relaxed" data-testid="basar-seller-note">
          {t("Numrene blir dine når Vipps-betalingen er godkjent. Kjøpet er bindende og betalingen går direkte til {org} – Right in formidler bare kjøpet.", { org: basar.organizer_name })}{" "}
          <Link to="/vilkar" className="underline underline-offset-4 hover:text-foreground transition-colors duration-150" data-testid="basar-terms-link">{t("Vilkår")}</Link>
        </p>
      </div>

      <Alert className="bg-[hsl(var(--warning))]/10 border-[hsl(var(--warning))]/30" data-testid="basar-min-tickets-hint">
        <Info className="h-4 w-4" />
        <AlertDescription className="text-xs">
          {min > 1 ? `${t("Minimum {n} lodd per kjøp.", { n: min })} ` : ""}{t("Ingen egen lagre-knapp: valgte nummer blir holdt av i 15 minutter først når du trykker «Betal med Vipps», og frigis automatisk hvis betalingen ikke fullføres.")}
        </AlertDescription>
      </Alert>
      </>
      )}
    </form>
  );
};
